import React, { useEffect, useState } from 'react';
import { DataService } from '../services/dataService';
import { GameItem, CalendarEvent, PageView } from '../types';
import { Calendar, Eye, Users, Bot, Swords, Scroll, Trophy, Crown, Gem, Shield, BookOpen, Skull, Flame, Search, HelpCircle, Sparkles, Dices } from 'lucide-react';

export type GamingView = 'lounge' | 'library' | 'vr' | 'events' | 'membership' | 'ranking';

interface GamingPageProps {
  initialView?: GamingView;
  onNavigate: (page: PageView) => void;
}

export const GamingPage: React.FC<GamingPageProps> = ({ initialView = 'lounge', onNavigate }) => {
  const [view, setView] = useState<GamingView>(initialView);
  const [games, setGames] = useState<GameItem[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  
  // Library filters
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Vše');

  useEffect(() => {
    setGames(DataService.getGames());
    setEvents(DataService.getEvents());
  }, []);

  // Unique genres for filter
  const genres = ['Vše', ...Array.from(new Set(games.map(g => g.genre)))];

  const filteredGames = games.filter(game => {
    const matchesSearch = game.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'Vše' || game.genre === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const rankingPlayers = [
    { name: "Gandalf_Bruntál", points: 2450, game: "Magic: The Gathering", avatar: "🧙‍♂️", title: "Arcimág" },
    { name: "Kostěj_Nesmrtelný", points: 2100, game: "Warhammer 40k", avatar: "💀", title: "Vojevůdce" },
    { name: "Eliška_z_Riviery", points: 1950, game: "D&D 5e", avatar: "🧝‍♀️", title: "Bard" },
    { name: "CyberPunk_Jirka", points: 1800, game: "Netrunner", avatar: "🤖", title: "Hacker" },
    { name: "Drakobijce123", points: 1500, game: "Catan", avatar: "🐉", title: "Osadník" },
  ];

  return (
    <div className="min-h-screen bg-gaming-bg text-gaming-text font-sans flex flex-col">
      
      {/* Sub-Navigation - Dark & Modern */}
      <div className="bg-gaming-bg border-b border-white/10 sticky top-20 z-40 shadow-xl backdrop-blur-md overflow-x-auto scrollbar-hide">
         <div className="max-w-7xl mx-auto flex justify-center min-w-max">
            {['lounge', 'library', 'events', 'ranking', 'membership', 'vr'].map((item) => (
                <button 
                    key={item}
                    onClick={() => setView(item as GamingView)} 
                    className={`px-4 md:px-6 py-4 border-b-2 font-sans font-bold uppercase tracking-widest text-xs md:text-xs transition-all 
                    ${view === item 
                        ? 'border-gaming-accent text-gaming-accent' 
                        : 'border-transparent text-gray-500 hover:text-white'
                    }`}
                >
                    {item === 'lounge' ? 'Doupě' : 
                     item === 'library' ? 'Knihovna' :
                     item === 'events' ? 'Akce' :
                     item === 'ranking' ? 'Žebříček' :
                     item === 'membership' ? 'Členství' : 'VR'}
                </button>
            ))}
            <button onClick={() => onNavigate('RESERVATION')} className="px-4 md:px-6 py-4 border-b-2 border-transparent text-gaming-accent hover:text-white font-sans font-bold uppercase tracking-widest text-xs md:text-xs transition-all">
                Rezervace
            </button>
         </div>
      </div>

      <div className="animate-fade-in flex-grow">

        {/* VIEW: LOUNGE (Home) */}
        {view === 'lounge' && (
            <>
                <section className="relative h-[80vh] flex items-center justify-center overflow-hidden border-b border-white/10 group">
                     <div className="absolute inset-0 bg-black/60 z-10" />
                     <img src="https://images.unsplash.com/photo-1606167668584-78701c57f13d?auto=format&fit=crop&q=80&w=1920" alt="Board Game Setup" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[20s] transform scale-100 group-hover:scale-105" />
                     
                     <div className="relative z-20 text-center px-4 animate-fade-in max-w-5xl">
                        <div className="inline-flex items-center gap-2 mb-8 bg-white/5 px-6 py-2 rounded-full border border-white/10 backdrop-blur-md">
                            <span className="text-gaming-accent text-xs font-bold uppercase tracking-[0.2em]">Open: 14:00 - 00:00</span>
                        </div>
                        <h1 className="text-6xl md:text-9xl font-display font-bold text-white mb-6 tracking-wide leading-none">
                           HERNÍ DOUPĚ
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 font-sans font-light max-w-2xl mx-auto mb-12">
                            Útočiště pro stratégy, snílky a hrdiny. Největší sbírka her v regionu.
                        </p>
                        <div className="flex flex-col md:flex-row justify-center gap-6">
                            <button onClick={() => setView('library')} className="px-10 py-4 bg-gaming-accent text-black font-bold uppercase tracking-widest hover:bg-white transition-all rounded-sm flex items-center justify-center gap-2">
                                <BookOpen size={16} /> Knihovna Her
                            </button>
                            <button onClick={() => onNavigate('RESERVATION')} className="px-10 py-4 bg-transparent border border-white text-white font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors rounded-sm flex items-center justify-center gap-2">
                                <Calendar size={16} /> Rezervovat Stůl
                            </button>
                        </div>
                     </div>
                </section>

                <div className="max-w-7xl mx-auto px-6 py-24">
                     {/* Feature Cards */}
                     <div className="grid md:grid-cols-3 gap-8 mb-32">
                        <div className="bg-gaming-card p-10 rounded-sm border border-white/5 hover:border-gaming-accent transition-all duration-300 group">
                            <Swords className="w-12 h-12 text-gaming-accent mb-6" />
                            <h3 className="text-xl font-display font-bold text-white mb-4">Karetní Hry</h3>
                            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                                Oficiální WPN prodejna. Magic: The Gathering, Pokémon TCG a Flesh and Blood. Turnaje každý pátek.
                            </p>
                        </div>

                        <div className="bg-gaming-card p-10 rounded-sm border border-white/5 hover:border-gaming-accent transition-all duration-300 group">
                            <Scroll className="w-12 h-12 text-gaming-accent mb-6" />
                            <h3 className="text-xl font-display font-bold text-white mb-4">RPG & D&D</h3>
                            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                                Privátní odhlučněné místnosti. Ideální pro Dungeons & Dragons, Jeskyně a Draky nebo Call of Cthulhu.
                            </p>
                        </div>

                        <div className="bg-gaming-card p-10 rounded-sm border border-white/5 hover:border-gaming-accent transition-all duration-300 group">
                            <Skull className="w-12 h-12 text-gaming-accent mb-6" />
                            <h3 className="text-xl font-display font-bold text-white mb-4">Wargaming</h3>
                            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                                4 masivní stoly (6x4 stopy) plné detailního terénu. Warhammer 40k, Age of Sigmar a Kill Team.
                            </p>
                        </div>
                     </div>
                </div>
            </>
        )}

        {/* VIEW: LIBRARY */}
        {view === 'library' && (
             <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12">
                    <h2 className="text-3xl font-display font-bold text-white">Knihovna Her</h2>
                    <div className="relative w-full md:w-80">
                        <input 
                            type="text" 
                            placeholder="Hledat hru..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-gaming-card border border-white/10 text-white rounded-sm py-3 pl-10 pr-4 focus:outline-none focus:border-gaming-accent"
                        />
                        <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" />
                    </div>
                </div>

                <div className="flex gap-2 overflow-x-auto pb-6 mb-6 scrollbar-hide">
                    {genres.map(genre => (
                        <button 
                            key={genre}
                            onClick={() => setActiveCategory(genre)}
                            className={`px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors border 
                            ${activeCategory === genre 
                                ? 'bg-gaming-accent text-black border-gaming-accent' 
                                : 'bg-transparent text-gray-400 border-white/10 hover:text-white hover:border-white/30'}`}
                        >
                            {genre}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {filteredGames.map(game => (
                        <div key={game.id} className="bg-gaming-card border border-white/5 rounded-sm overflow-hidden group hover:border-gaming-accent transition-all duration-500">
                            <div className="h-48 overflow-hidden relative">
                                <img src={game.image} alt={game.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 grayscale group-hover:grayscale-0" />
                                <div className="absolute top-2 right-2 bg-black/80 text-white text-[10px] font-bold px-2 py-1 uppercase tracking-wider">
                                    {game.genre}
                                </div>
                            </div>
                            <div className="p-5">
                                <h3 className="text-lg font-bold text-white mb-2 truncate">{game.title}</h3>
                                <div className="flex justify-between items-center pt-4 border-t border-white/5">
                                    <span className={`text-xs font-bold uppercase tracking-wider ${game.price > 0 ? 'text-green-400' : 'text-blue-400'}`}>
                                        {game.price > 0 ? 'Na Prodej' : 'K zapůjčení'}
                                    </span>
                                    <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className={`w-1 h-1 rounded-full ${i < game.rating ? 'bg-gaming-accent' : 'bg-gray-700'}`}></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* VIEW: EVENTS */}
        {view === 'events' && (
             <div className="max-w-4xl mx-auto px-6 py-12">
                 <h2 className="text-3xl font-display font-bold text-white mb-10 text-center">Kalendář Akcí</h2>
                 <div className="space-y-4">
                    {events.map(event => (
                    <div key={event.id} className="bg-gaming-card border border-white/5 p-6 rounded-sm hover:border-gaming-accent transition-colors flex flex-col md:flex-row gap-6 items-center">
                        <div className="text-center md:text-left min-w-[80px]">
                            <div className="text-gaming-accent font-bold text-xl">{new Date(event.date).getDate()}</div>
                            <div className="text-gray-500 text-xs uppercase tracking-wider">{new Date(event.date).toLocaleDateString('cs-CZ', { month: 'short' })}</div>
                        </div>
                        <div className="flex-grow text-center md:text-left">
                            <h3 className="text-lg font-bold text-white mb-1">{event.title}</h3>
                            <p className="text-gray-400 text-sm">{event.description}</p>
                        </div>
                        <button className="px-6 py-2 border border-white/20 text-white text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors rounded-sm">
                            Detail
                        </button>
                    </div>
                    ))}
                 </div>
             </div>
        )}

         {/* VIEW: RANKING */}
         {view === 'ranking' && (
            <div className="max-w-3xl mx-auto px-6 py-12">
                <div className="text-center mb-12">
                     <h2 className="text-3xl font-display font-bold text-white mb-2">Síň Slávy</h2>
                     <p className="text-gray-400 font-light text-sm">Nejlepší hráči měsíce</p>
                </div>
                <div className="bg-gaming-card border border-white/10 rounded-sm overflow-hidden">
                    {rankingPlayers.map((player, index) => (
                        <div key={index} className="flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                            <div className="flex items-center gap-4">
                                <span className={`font-display font-bold text-xl w-8 text-center ${index === 0 ? 'text-gaming-accent' : 'text-gray-600'}`}>{index + 1}.</span>
                                <div className="text-2xl">{player.avatar}</div>
                                <div>
                                    <div className="text-white font-bold text-sm">{player.name}</div>
                                    <div className="text-gray-500 text-xs">{player.title}</div>
                                </div>
                            </div>
                            <div className="text-gaming-accent font-bold font-mono">{player.points} XP</div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* VIEW: VR */}
        {view === 'vr' && (
            <div className="max-w-4xl mx-auto px-6 py-12 text-center">
                <h2 className="text-3xl font-display font-bold text-white mb-8">Virtuální Realita</h2>
                <div className="grid md:grid-cols-2 gap-8">
                     <div className="bg-gaming-card border border-white/10 p-8 rounded-sm">
                         <h3 className="text-xl font-bold text-white mb-4">Meta Quest 3</h3>
                         <p className="text-gray-400 text-sm mb-6">Nejnovější generace VR. Bezdrátová svoboda a dokonalý obraz.</p>
                         <div className="text-gaming-accent font-bold text-2xl mb-1">150 Kč</div>
                         <div className="text-gray-500 text-xs uppercase tracking-widest">30 Minut</div>
                     </div>
                     <div className="bg-gaming-card border border-white/10 p-8 rounded-sm">
                         <h3 className="text-xl font-bold text-white mb-4">Beat Saber Zóna</h3>
                         <p className="text-gray-400 text-sm mb-6">Soutěžte s přáteli o nejvyšší skóre na velké obrazovce.</p>
                         <div className="text-gaming-accent font-bold text-2xl mb-1">250 Kč</div>
                         <div className="text-gray-500 text-xs uppercase tracking-widest">60 Minut</div>
                     </div>
                </div>
            </div>
        )}

         {/* VIEW: MEMBERSHIP */}
         {view === 'membership' && (
            <div className="max-w-4xl mx-auto px-6 py-12">
                 <h2 className="text-3xl font-display font-bold text-white mb-10 text-center">Členství</h2>
                 <div className="grid md:grid-cols-3 gap-6">
                     <div className="bg-gaming-card border border-white/10 p-8 rounded-sm text-center">
                         <h3 className="text-white font-bold mb-2">Poutník</h3>
                         <div className="text-2xl text-gaming-accent font-bold mb-4">50 Kč <span className="text-xs text-gray-500 font-normal">/ den</span></div>
                         <p className="text-gray-400 text-xs mb-6">Vstup a 1 hra</p>
                         <button className="w-full py-2 border border-white/20 text-white text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors rounded-sm">Vybrat</button>
                     </div>
                     <div className="bg-white/10 border border-gaming-accent p-8 rounded-sm text-center transform scale-105 shadow-glow">
                         <h3 className="text-gaming-accent font-bold mb-2">Hrdina</h3>
                         <div className="text-3xl text-white font-bold mb-4">100 Kč <span className="text-xs text-gray-400 font-normal">/ den</span></div>
                         <p className="text-gray-300 text-xs mb-6">Všechny hry + Drink</p>
                         <button className="w-full py-3 bg-gaming-accent text-black text-xs uppercase tracking-widest hover:bg-white transition-colors rounded-sm font-bold">Vybrat</button>
                     </div>
                     <div className="bg-gaming-card border border-white/10 p-8 rounded-sm text-center">
                         <h3 className="text-purple-400 font-bold mb-2">Legenda</h3>
                         <div className="text-2xl text-white font-bold mb-4">800 Kč <span className="text-xs text-gray-500 font-normal">/ měsíc</span></div>
                         <p className="text-gray-400 text-xs mb-6">Neomezený vstup</p>
                         <button className="w-full py-2 border border-purple-500 text-purple-400 text-xs uppercase tracking-widest hover:bg-purple-500 hover:text-white transition-colors rounded-sm">Vybrat</button>
                     </div>
                 </div>
            </div>
        )}

      </div>

      <footer className="bg-black text-gray-500 py-12 border-t border-white/10 text-center text-xs uppercase tracking-widest">
        © 2023 Kavárna na náměstí • Herní Doupě Bruntál
      </footer>
    </div>
  );
};